import { useCallback, useEffect, useState } from "react";
import { ApiError, apiGet } from "./customFetch";

export interface Me {
  navn: string;
  rolle: "ejer" | "administrator" | "bogholder" | "medarbejder";
  tenantNavn: string;
  /** Anonym nøgle til at isolere lokale stempeldata pr. bruger. */
  clockinScope?: string;
  /** M-10: medarbejderens registrerings-mode (styrer /stemple-fladen). */
  registreringsMode?: "stempel" | "standard";
}

export type UserRole = Me["rolle"];

export const ROLLE_TEKST: Readonly<Record<UserRole, string>> = {
  ejer: "Ejer",
  administrator: "Administrator",
  bogholder: "Bogholder",
  medarbejder: "Medarbejder",
};

export interface MeState {
  me: Me | null;
  loading: boolean;
  error: "unauthorized" | "failed" | null;
  reload: () => void;
}

type MeSnapshot = Omit<MeState, "reload">;

/**
 * Profil-/virksomhedsændringer annonceres med dette event, så alle
 * useMe-instanser (fx AppShells identitetsblok) genhenter identiteten.
 */
const ME_UPDATED_EVENT = "appsmeden:me-updated";

export function notifyMeUpdated(): void {
  window.dispatchEvent(new Event(ME_UPDATED_EVENT));
}

/** Hvem er jeg? Bruges af AppShell til identitet, core-nav og redirect. */
export function useMe(): MeState {
  const [state, setState] = useState<MeSnapshot>({
    me: null,
    loading: true,
    error: null,
  });
  const [version, setVersion] = useState(0);
  const reload = useCallback(() => setVersion((value) => value + 1), []);

  useEffect(() => {
    const genhent = () => reload();
    window.addEventListener(ME_UPDATED_EVENT, genhent);
    return () => window.removeEventListener(ME_UPDATED_EVENT, genhent);
  }, [reload]);

  useEffect(() => {
    let cancelled = false;
    apiGet<Me>("/api/me")
      .then((me) => {
        if (!cancelled) setState({ me, loading: false, error: null });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const error =
          err instanceof ApiError && err.status === 401
            ? ("unauthorized" as const)
            : ("failed" as const);
        setState({ me: null, loading: false, error });
      });
    return () => {
      cancelled = true;
    };
  }, [version]);

  return { ...state, reload };
}
